<template>
  <div class="data-search-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <h1 class="page-title">Genomic Data Browser</h1>
      <p class="page-subtitle">Search and explore genetic variants, genes, and genomic regions</p>
    </div>

    <!-- 主搜索栏和数据类型选择 -->
    <div class="search-section">
      <el-card class="search-card">
        <div class="search-header">
          <el-input
            v-model="searchQuery"
            placeholder="Search by gene name, variant ID, rsID, or position (e.g., chr1:12345)"
            size="large"
            class="search-input"
            @keyup.enter="performSearch"
          >
            <template #prepend>
              <el-select v-model="activeDataType" style="width: 150px" @change="onDataTypeChange">
                <el-option label="Variants" value="variants" />
                <el-option label="Genes" value="genes" />
                <el-option label="Haplotypes" value="haplotypes" />
                <el-option label="AMR Regions" value="amr" />
                <el-option label="Allele Config" value="allele-config" />
              </el-select>
            </template>
            <template #append>
              <el-button @click="performSearch" :loading="loading">
                <el-icon><Search /></el-icon>
              </el-button>
            </template>
          </el-input>
        </div>

        <!-- 快速操作按钮 -->
        <div class="quick-actions">
          <el-button-group>
            <el-button @click="loadOverviewStats" :loading="statsLoading">
              <el-icon><PieChart /></el-icon>
              Statistics
            </el-button>
            <el-button @click="showExportDialog = true" :disabled="!tableData.length">
              <el-icon><Download /></el-icon>
              Export
            </el-button>
            <el-button @click="showBatchDialog = true">
              <el-icon><Operation /></el-icon>
              Batch Query
            </el-button>
            <el-button @click="toggleFilterPanel">
              <el-icon><Filter /></el-icon>
              Filters
            </el-button>
          </el-button-group>
        </div>
      </el-card>
    </div>

    <div class="main-content">
      <!-- 左侧筛选面板 -->
      <transition name="slide-left">
        <div v-if="showFilters" class="filter-panel">
          <el-card class="filter-card">
            <template #header>
              <div class="filter-header">
                <span>Advanced Filters</span>
                <el-button text @click="clearAllFilters">Clear All</el-button>
              </div>
            </template>

            <!-- 通用过滤器 -->
            <div class="filter-section">
              <h4>Chromosome</h4>
              <el-select v-model="filters.chrom" placeholder="Select chromosome" clearable>
                <el-option v-for="chr in chromosomes" :key="chr" :label="chr" :value="chr" />
              </el-select>
            </div>

            <!-- 变异特定过滤器 -->
            <div v-if="activeDataType === 'variants'" class="filter-section">
              <h4>Position Range</h4>
              <el-input v-model="filters.position_start" placeholder="Start position" type="number" />
              <el-input v-model="filters.position_end" placeholder="End position" type="number" style="margin-top: 8px" />

              <h4>rsID</h4>
              <el-input v-model="filters.rsid" placeholder="e.g., rs123456" />

              <h4>Quality Filter</h4>
              <el-slider v-model="filters.min_quality" :min="0" :max="100" show-input />

              <h4>Allele Count</h4>
              <el-input-number v-model="filters.min_alt_count" :min="0" placeholder="Min alt count" />
            </div>

            <!-- 基因特定过滤器 -->
            <div v-if="activeDataType === 'genes'" class="filter-section">
              <h4>Gene Name</h4>
              <el-input v-model="filters.gene_name" placeholder="e.g., BRCA1" />

              <h4>Ensembl ID</h4>
              <el-input v-model="filters.ensembl_id" placeholder="e.g., ENSG00000012048" />
            </div>

            <!-- 单倍型特定过滤器 -->
            <div v-if="activeDataType === 'haplotypes'" class="filter-section">
              <h4>Minimum Variants</h4>
              <el-input-number v-model="filters.min_variants" :min="1" />

              <h4>Minimum Reads</h4>
              <el-input-number v-model="filters.min_reads" :min="0" />
            </div>

            <!-- AMR区域特定过滤器 -->
            <div v-if="activeDataType === 'amr'" class="filter-section">
              <h4>Maximum P-value</h4>
              <el-input v-model="filters.max_pvalue" type="number" step="0.001" placeholder="e.g., 0.05" />

              <h4>Minimum CpG Count</h4>
              <el-input-number v-model="filters.min_cpg_count" :min="0" />
            </div>

            <!-- 区域查询 -->
            <div class="filter-section">
              <h4>Region Query</h4>
              <el-input v-model="regionQuery.chrom" placeholder="Chromosome" />
              <el-input v-model="regionQuery.start" placeholder="Start position" type="number" style="margin-top: 8px" />
              <el-input v-model="regionQuery.end" placeholder="End position" type="number" style="margin-top: 8px" />
              <el-button @click="queryRegion" style="margin-top: 8px; width: 100%">Query Region</el-button>
            </div>

            <div class="filter-actions">
              <el-button type="primary" @click="applyFilters" :loading="loading">Apply Filters</el-button>
              <el-button @click="resetFilters">Reset</el-button>
            </div>
          </el-card>
        </div>
      </transition>

      <!-- 右侧数据表格区域 -->
      <div class="table-section" :class="{ 'full-width': !showFilters }">
        <!-- 表格工具栏 -->
        <div class="table-toolbar">
          <div class="toolbar-left">
            <span class="data-count">
              Total: {{ pagination.total }} {{ activeDataType }}
              <el-tag v-if="searchQuery" type="info" size="small">Search: "{{ searchQuery }}"</el-tag>
            </span>
          </div>
          <div class="toolbar-right">
            <el-select v-model="pagination.pageSize" @change="loadData" style="width: 100px">
              <el-option label="20" :value="20" />
              <el-option label="50" :value="50" />
              <el-option label="100" :value="100" />
              <el-option label="200" :value="200" />
            </el-select>
            <el-button @click="refreshData" :loading="loading">
              <el-icon><Refresh /></el-icon>
            </el-button>
          </div>
        </div>

        <!-- 数据表格 -->
        <el-table
          :data="tableData"
          v-loading="loading"
          style="width: 100%"
          @selection-change="handleSelectionChange"
          @row-click="handleRowClick"
          stripe
          border
          height="600"
        >
          <el-table-column type="selection" width="55" />
          <el-table-column type="index" width="60" />

          <!-- 变异表格列 -->
          <template v-if="activeDataType === 'variants'">
            <el-table-column prop="variant_id" label="Variant ID" min-width="120" />
            <el-table-column prop="chrom" label="Chr" width="80" />
            <el-table-column prop="position" label="Position" width="120" />
            <el-table-column prop="ref_allele" label="Ref" width="80" />
            <el-table-column prop="alt_allele" label="Alt" width="80" />
            <el-table-column prop="rsid" label="rsID" width="120" />
          </template>

          <!-- 基因表格列 -->
          <template v-if="activeDataType === 'genes'">
            <el-table-column prop="gene_id" label="Gene ID" min-width="120" />
            <el-table-column prop="gene_name" label="Gene Name" width="120" />
            <el-table-column prop="ensembl_id" label="Ensembl ID" min-width="150" />
            <el-table-column prop="chrom" label="Chr" width="80" />
            <el-table-column prop="start_pos" label="Start" width="120" />
            <el-table-column prop="stop_pos" label="End" width="120" />
            <el-table-column prop="haplotype_count_records" label="Haplotype Records" width="140" />
          </template>

          <!-- 单倍型表格列 -->
          <template v-if="activeDataType === 'haplotypes'">
            <el-table-column prop="haplotype_id" label="Haplotype ID" width="120" />
            <el-table-column prop="chrom" label="Chr" width="80" />
            <el-table-column prop="start_pos" label="Start" width="120" />
            <el-table-column prop="stop_pos" label="End" width="120" />
            <el-table-column prop="length" label="Length" width="100" />
            <el-table-column prop="variant_count" label="Variants" width="100" />
            <el-table-column prop="reads_total" label="Total Reads" width="120" />
            <el-table-column prop="hap_a_percentage" label="Hap A %" width="100" />
            <el-table-column prop="hap_b_percentage" label="Hap B %" width="100" />
          </template>

          <!-- AMR区域表格列 -->
          <template v-if="activeDataType === 'amr'">
            <el-table-column prop="region_id" label="Region ID" width="120" />
            <el-table-column prop="chrom" label="Chr" width="80" />
            <el-table-column prop="start_pos" label="Start" width="120" />
            <el-table-column prop="stop_pos" label="End" width="120" />
            <el-table-column prop="length" label="Length" width="100" />
            <el-table-column prop="cpg_count" label="CpG Count" width="100" />
            <el-table-column prop="p_value" label="P-value" width="120">
              <template #default="scope">
                {{ scope.row.p_value ? parseFloat(scope.row.p_value).toExponential(2) : 'N/A' }}
              </template>
            </el-table-column>
          </template>

          <!-- 等位基因配置表格列 -->
          <template v-if="activeDataType === 'allele-config'">
            <el-table-column prop="config_id" label="Config ID" width="120" />
            <el-table-column prop="configuration" label="Configuration" width="150" />
            <el-table-column prop="chrom_a" label="Chr A" width="80" />
            <el-table-column prop="position_a" label="Pos A" width="100" />
            <el-table-column prop="rsid_a" label="rsID A" width="120" />
            <el-table-column prop="chrom_b" label="Chr B" width="80" />
            <el-table-column prop="position_b" label="Pos B" width="100" />
            <el-table-column prop="rsid_b" label="rsID B" width="120" />
          </template>

          <el-table-column label="Actions" width="120">
            <template #default="scope">
              <el-button link type="primary" @click="viewDetails(scope.row)">View</el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页组件 -->
        <div class="pagination-wrapper">
          <el-pagination
            v-model:current-page="pagination.currentPage"
            v-model:page-size="pagination.pageSize"
            :page-sizes="[20, 50, 100, 200, 500]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </div>

    <!-- 详情抽屉 -->
    <el-drawer
      v-model="showDetailsDrawer"
      :title="`${activeDataType.charAt(0).toUpperCase() + activeDataType.slice(1)} Details`"
      direction="rtl"
      size="50%"
    >
      <div v-if="selectedItem" class="details-content">
        <el-descriptions :column="2" border>
          <template v-for="(value, key) in selectedItem" :key="key">
            <el-descriptions-item v-if="!isComplexObject(value)" :label="formatLabel(key)">
              {{ formatValue(value) }}
            </el-descriptions-item>
          </template>
        </el-descriptions>

        <!-- 复杂对象的展示 -->
        <div v-if="selectedItem.allelic_counts" class="complex-section">
          <h3>Allelic Counts</h3>
          <el-table :data="formatAllelicCounts(selectedItem.allelic_counts)" border>
            <el-table-column prop="sample" label="Sample" />
            <el-table-column prop="ref_count" label="Ref Count" />
            <el-table-column prop="alt_count" label="Alt Count" />
            <el-table-column prop="total_count" label="Total Count" />
          </el-table>
        </div>

        <div v-if="selectedItem.sequencing_data" class="complex-section">
          <h3>Sequencing Data</h3>
          <el-table :data="selectedItem.sequencing_data" border>
            <el-table-column prop="data_source" label="Data Source" />
            <el-table-column prop="quality" label="Quality" />
            <el-table-column prop="filter_status" label="Filter Status" />
            <el-table-column prop="genotype" label="Genotype" />
          </el-table>
        </div>

        <div v-if="selectedItem.haplotype_counts" class="complex-section">
          <h3>Haplotype Counts</h3>
          <el-table :data="selectedItem.haplotype_counts.slice(0, 10)" border>
            <el-table-column prop="binom_p_value" label="P-value" />
            <el-table-column prop="count_id" label="Count ID" />
          </el-table>
        </div>
      </div>
    </el-drawer>

    <!-- 导出对话框 -->
    <el-dialog v-model="showExportDialog" title="Export Data" width="500px">
      <el-form :model="exportForm" label-position="top">
        <el-form-item label="Export Format">
          <el-radio-group v-model="exportForm.format">
            <el-radio label="json">JSON</el-radio>
            <el-radio label="csv">CSV</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="Limit Records">
          <el-input-number v-model="exportForm.limit" :min="1" :max="100000" />
        </el-form-item>
        <el-form-item label="Filter by Chromosome (Optional)">
          <el-select v-model="exportForm.chrom" clearable>
            <el-option v-for="chr in chromosomes" :key="chr" :label="chr" :value="chr" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showExportDialog = false">Cancel</el-button>
        <el-button type="primary" @click="exportData" :loading="exportLoading">Export</el-button>
      </template>
    </el-dialog>

    <!-- 批量查询对话框 -->
    <el-dialog v-model="showBatchDialog" title="Batch Query" width="600px">
      <el-form :model="batchForm" label-position="top">
        <el-form-item label="Variant IDs (one per line, max 1000)">
          <el-input
            v-model="batchForm.ids"
            type="textarea"
            :rows="10"
            placeholder="Enter variant IDs, one per line..."
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showBatchDialog = false">Cancel</el-button>
        <el-button type="primary" @click="batchQuery" :loading="batchLoading">Query</el-button>
      </template>
    </el-dialog>

    <!-- 统计概览对话框 -->
    <el-dialog v-model="showStatsDialog" title="Database Statistics" width="800px">
      <div v-if="statsData" class="stats-content">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-card>
              <template #header>Table Counts</template>
              <el-descriptions :column="1">
                <el-descriptions-item v-for="(stat, key) in statsData" :key="key" v-if="stat.count" :label="stat.description">
                  {{ stat.count.toLocaleString() }}
                </el-descriptions-item>
              </el-descriptions>
            </el-card>
          </el-col>
          <el-col :span="12">
            <el-card>
              <template #header>Chromosome Distribution</template>
              <div style="max-height: 300px; overflow-y: auto;">
                <el-table :data="statsData.chromosome_distribution" size="small">
                  <el-table-column prop="chrom" label="Chromosome" width="100" />
                  <el-table-column prop="count" label="Variants" />
                </el-table>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, PieChart, Download, Operation, Filter, Refresh } from '@element-plus/icons-vue'

export default {
  name: 'DataSearch',
  components: {
    Search,
    PieChart,
    Download,
    Operation,
    Filter,
    Refresh
  },
  setup() {
    // 基础状态管理
    const searchQuery = ref('')
    const activeDataType = ref('variants')
    const loading = ref(false)
    const showFilters = ref(true)
    const tableData = ref([])
    const selectedRows = ref([])
    const selectedItem = ref(null)
    const showDetailsDrawer = ref(false)

    // 对话框状态
    const showExportDialog = ref(false)
    const showBatchDialog = ref(false)
    const showStatsDialog = ref(false)
    const exportLoading = ref(false)
    const batchLoading = ref(false)
    const statsLoading = ref(false)
    const statsData = ref(null)

    // 分页状态
    const pagination = reactive({
      currentPage: 1,
      pageSize: 50,
      total: 0
    })

    // 过滤器状态
    const filters = reactive({
      chrom: '',
      rsid: '',
      position_start: '',
      position_end: '',
      gene_name: '',
      ensembl_id: '',
      min_variants: null,
      min_reads: null,
      max_pvalue: '',
      min_cpg_count: null,
      min_quality: 0,
      min_alt_count: null
    })

    // 区域查询状态
    const regionQuery = reactive({
      chrom: '',
      start: '',
      end: ''
    })

    // 导出表单
    const exportForm = reactive({
      format: 'json',
      limit: 10000,
      chrom: ''
    })

    // 批量查询表单
    const batchForm = reactive({
      ids: ''
    })

    // 染色体列表
    const chromosomes = ref([
      '1', '2', '3', '4', '5', '6', '7', '8', '9', '10',
      '11', '12', '13', '14', '15', '16', '17', '18', '19', '20',
      '21', '22', 'X', 'Y', 'MT'
    ])

    // API基础URL
    const API_BASE = 'https://allelebc.foxhank.cn/api'

    // 修复：构建查询参数的辅助函数
    const buildQueryString = (params = {}) => {
      const searchParams = new URLSearchParams()
      Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
          searchParams.append(key, params[key])
        }
      })
      const queryString = searchParams.toString()
      return queryString ? `?${queryString}` : ''
    }

    // 修复：生成完整URL的辅助函数
    const buildApiUrl = (endpoint, params = {}) => {
      return `${API_BASE}${endpoint}${buildQueryString(params)}`
    }

    // 数据加载函数
    const loadData = async () => {
      loading.value = true
      try {
        let endpoint = ''
        let params = {
          page: pagination.currentPage,
          per_page: pagination.pageSize
        }

        // 根据数据类型构建不同的endpoint
        switch (activeDataType.value) {
          case 'variants':
            endpoint = '/variants'
            break
          case 'genes':
            endpoint = '/genes'
            break
          case 'haplotypes':
            endpoint = '/haplotypes'
            break
          case 'amr':
            endpoint = '/amr-regions'
            break
          case 'allele-config':
            endpoint = '/allele-configurations'
            break
        }

        // 添加过滤参数
        Object.keys(filters).forEach(key => {
          if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
            params[key] = filters[key]
          }
        })

        const response = await fetch(buildApiUrl(endpoint, params))
        const data = await response.json()

        if (response.ok) {
          tableData.value = data.data || []
          pagination.total = data.total || 0
        } else {
          ElMessage.error(data.error || 'Failed to load data')
        }
      } catch (error) {
        console.error('Error loading data:', error)
        ElMessage.error('Network error occurred')
      } finally {
        loading.value = false
      }
    }

    // 搜索功能
    const performSearch = async () => {
      if (!searchQuery.value.trim()) {
        loadData()
        return
      }

      loading.value = true
      try {
        let endpoint = ''
        switch (activeDataType.value) {
          case 'variants':
            endpoint = '/search/variants'
            break
          case 'genes':
            endpoint = '/search/genes'
            break
          default:
            // 对于其他类型，使用常规加载但应用搜索词作为过滤器
            loadData()
            return
        }

        const params = {
          q: searchQuery.value,
          limit: pagination.pageSize
        }

        const response = await fetch(buildApiUrl(endpoint, params))
        const data = await response.json()

        if (response.ok) {
          tableData.value = data.results || []
          pagination.total = data.count || 0
        } else {
          ElMessage.error(data.error || 'Search failed')
        }
      } catch (error) {
        console.error('Error performing search:', error)
        ElMessage.error('Search error occurred')
      } finally {
        loading.value = false
      }
    }

    // 数据类型变化处理
    const onDataTypeChange = () => {
      pagination.currentPage = 1
      clearAllFilters()
      loadData()
    }

    // 过滤器相关函数
    const toggleFilterPanel = () => {
      showFilters.value = !showFilters.value
    }

    const clearAllFilters = () => {
      Object.keys(filters).forEach(key => {
        if (typeof filters[key] === 'string') {
          filters[key] = ''
        } else {
          filters[key] = null
        }
      })
    }

    const resetFilters = () => {
      clearAllFilters()
      loadData()
    }

    const applyFilters = () => {
      pagination.currentPage = 1
      loadData()
    }

    // 区域查询
    const queryRegion = async () => {
      if (!regionQuery.chrom || !regionQuery.start || !regionQuery.end) {
        ElMessage.warning('Please fill in all region fields')
        return
      }

      loading.value = true
      try {
        const endpoint = activeDataType.value === 'genes' ? '/region/genes' : '/region/variants'
        const response = await fetch(buildApiUrl(endpoint, regionQuery))
        const data = await response.json()

        if (response.ok) {
          tableData.value = data[activeDataType.value] || data.variants || []
          pagination.total = data[`${activeDataType.value}_count`] || data.variant_count || 0
          ElMessage.success(`Found ${pagination.total} records in region ${data.region}`)
        } else {
          ElMessage.error(data.error || 'Region query failed')
        }
      } catch (error) {
        console.error('Error querying region:', error)
        ElMessage.error('Region query error occurred')
      } finally {
        loading.value = false
      }
    }

    // 表格事件处理
    const handleSelectionChange = (selection) => {
      selectedRows.value = selection
    }

    const handleRowClick = (row) => {
      viewDetails(row)
    }

    const handleSizeChange = (size) => {
      pagination.pageSize = size
      loadData()
    }

    const handleCurrentChange = (page) => {
      pagination.currentPage = page
      loadData()
    }

    // 查看详情
    const viewDetails = async (row) => {
      loading.value = true
      try {
        let endpoint = ''
        let id = ''

        switch (activeDataType.value) {
          case 'variants':
            endpoint = `/variants/${row.variant_id}`
            break
          case 'genes':
            endpoint = `/genes/${row.gene_id}`
            break
          case 'haplotypes':
            endpoint = `/haplotypes/${row.haplotype_id}`
            break
          default:
            selectedItem.value = row
            showDetailsDrawer.value = true
            loading.value = false
            return
        }

        const response = await fetch(`${API_BASE}${endpoint}`)
        const data = await response.json()

        if (response.ok) {
          selectedItem.value = data
          showDetailsDrawer.value = true
        } else {
          ElMessage.error(data.error || 'Failed to load details')
        }
      } catch (error) {
        console.error('Error loading details:', error)
        ElMessage.error('Failed to load details')
      } finally {
        loading.value = false
      }
    }

    // 导出数据
    const exportData = async () => {
      exportLoading.value = true
      try {
        const params = {
          format: exportForm.format,
          limit: exportForm.limit
        }
        if (exportForm.chrom) {
          params.chrom = exportForm.chrom
        }

        const response = await fetch(buildApiUrl('/export/variants', params))

        if (exportForm.format === 'csv') {
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = 'variants.csv'
          a.click()
          window.URL.revokeObjectURL(url)
        } else {
          const data = await response.json()
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = 'variants.json'
          a.click()
          window.URL.revokeObjectURL(url)
        }

        ElMessage.success('Export completed successfully')
        showExportDialog.value = false
      } catch (error) {
        console.error('Error exporting data:', error)
        ElMessage.error('Export failed')
      } finally {
        exportLoading.value = false
      }
    }

    // 批量查询
    const batchQuery = async () => {
      const ids = batchForm.ids.split('\n').filter(id => id.trim()).map(id => id.trim())

      if (ids.length === 0) {
        ElMessage.warning('Please enter at least one variant ID')
        return
      }

      if (ids.length > 1000) {
        ElMessage.warning('Maximum 1000 variant IDs allowed')
        return
      }

      batchLoading.value = true
      try {
        const response = await fetch(`${API_BASE}/batch/variants`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ variant_ids: ids })
        })

        const data = await response.json()

        if (response.ok) {
          tableData.value = data.data || []
          pagination.total = data.found || 0
          ElMessage.success(`Found ${data.found} out of ${data.requested} variants`)
          showBatchDialog.value = false
        } else {
          ElMessage.error(data.error || 'Batch query failed')
        }
      } catch (error) {
        console.error('Error in batch query:', error)
        ElMessage.error('Batch query error occurred')
      } finally {
        batchLoading.value = false
      }
    }

    // 加载统计数据
    const loadOverviewStats = async () => {
      statsLoading.value = true
      try {
        const response = await fetch(`${API_BASE}/stats/overview`)
        const data = await response.json()

        if (response.ok) {
          statsData.value = data
          showStatsDialog.value = true
        } else {
          ElMessage.error('Failed to load statistics')
        }
      } catch (error) {
        console.error('Error loading stats:', error)
        ElMessage.error('Failed to load statistics')
      } finally {
        statsLoading.value = false
      }
    }

    // 刷新数据
    const refreshData = () => {
      loadData()
    }

    // 辅助函数
    const isComplexObject = (value) => {
      return typeof value === 'object' && value !== null && !Array.isArray(value)
    }

    const formatLabel = (key) => {
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    const formatValue = (value) => {
      if (value === null || value === undefined) return 'N/A'
      if (typeof value === 'number' && value.toString().includes('e')) {
        return parseFloat(value).toExponential(2)
      }
      return value.toString()
    }

    const formatAllelicCounts = (counts) => {
      return Object.entries(counts).map(([sample, data]) => ({
        sample,
        ...data
      }))
    }

    // 组件挂载后初始化
    onMounted(() => {
      loadData()
    })

    return {
      // 基础状态
      searchQuery,
      activeDataType,
      loading,
      showFilters,
      tableData,
      selectedRows,
      selectedItem,
      showDetailsDrawer,

      // 对话框状态
      showExportDialog,
      showBatchDialog,
      showStatsDialog,
      exportLoading,
      batchLoading,
      statsLoading,
      statsData,

      // 分页
      pagination,

      // 过滤器
      filters,
      regionQuery,

      // 表单
      exportForm,
      batchForm,

      // 数据
      chromosomes,

      // 方法
      loadData,
      performSearch,
      onDataTypeChange,
      toggleFilterPanel,
      clearAllFilters,
      resetFilters,
      applyFilters,
      queryRegion,
      handleSelectionChange,
      handleRowClick,
      handleSizeChange,
      handleCurrentChange,
      viewDetails,
      exportData,
      batchQuery,
      loadOverviewStats,
      refreshData,

      // 辅助函数
      isComplexObject,
      formatLabel,
      formatValue,
      formatAllelicCounts
    }
  }
}
</script>

<style scoped>
/* 主容器样式 */
.data-search-container {
  min-height: 100vh;
  background: #f5f7fa;
  padding: 20px;
}

/* 页面头部 */
.page-header {
  text-align: center;
  margin-bottom: 30px;
}

.page-title {
  font-size: 36px;
  color: #1f3a93;
  margin-bottom: 10px;
  font-weight: 600;
}

.page-subtitle {
  font-size: 16px;
  color: #666;
}

/* 搜索区域 */
.search-section {
  margin-bottom: 20px;
}

.search-card {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(31, 58, 147, 0.1);
}

.search-header {
  margin-bottom: 20px;
}

.search-input {
  width: 100%;
}

.quick-actions {
  display: flex;
  justify-content: center;
}

/* 主内容区域 */
.main-content {
  display: flex;
  gap: 20px;
}

/* 筛选面板 */
.filter-panel {
  width: 320px;
  flex-shrink: 0;
}

.filter-card {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(31, 58, 147, 0.1);
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-section {
  margin-bottom: 20px;
}

.filter-section h4 {
  margin: 0 0 8px 0;
  color: #1f3a93;
  font-size: 14px;
  font-weight: 600;
}

.filter-actions {
  margin-top: 30px;
}

/* 表格区域 */
.table-section {
  flex: 1;
  min-width: 0;
  transition: all 0.3s ease;
}

.table-section.full-width {
  width: 100%;
}

.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 15px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.data-count {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* 分页区域 */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* 详情抽屉 */
.details-content {
  padding: 20px;
}

.complex-section {
  margin-top: 30px;
}

.complex-section h3 {
  color: #1f3a93;
  margin-bottom: 15px;
  font-size: 18px;
}

/* 统计对话框 */
.stats-content {
  padding: 20px;
}

/* 过渡动画 */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.3s ease;
}

.slide-left-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

/* Element Plus样式覆写 */
:deep(.el-card__header) {
  background: #f8f9ff;
  border-bottom: 1px solid #e4e7ed;
}

:deep(.el-table th) {
  background: #f8f9ff !important;
  color: #1f3a93;
  font-weight: 600;
}

:deep(.el-button--primary) {
  background: #1f3a93;
  border-color: #1f3a93;
}

:deep(.el-button--primary:hover) {
  background: #2952b8;
  border-color: #2952b8;
}

:deep(.el-pagination.is-background .el-pager li:not(.is-disabled).is-active) {
  background-color: #1f3a93;
}

:deep(.el-select-dropdown__item.is-selected) {
  color: #1f3a93;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .filter-panel {
    width: 280px;
  }
}

@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }

  .filter-panel {
    width: 100%;
  }

  .quick-actions {
    flex-wrap: wrap;
    gap: 10px;
  }

  .toolbar-left,
  .toolbar-right {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .table-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>